import { useEffect, useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'

interface OrderItem {
  id: string
  product_id: string
  quantity: number
  price_at_purchase: number
  product?: {
    name: string
  }
}

interface ShippingAddress {
  full_name: string
  street_address: string
  city: string
  state: string
  postal_code: string
  country: string
  phone: string
  email: string
  subtotal: number
  shipping_cost: number
  tax_amount: number
}

interface Order {
  id: string
  customer_id: string | null
  guest_email?: string
  guest_name?: string
  is_guest_order?: boolean
  total_amount: number
  status: string
  shipping_address: ShippingAddress
  created_at: string
  updated_at: string
}

export default function OrderConfirmation() {
  const { orderId } = useParams<{ orderId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [order, setOrder] = useState<Order | null>(null)
  const [orderItems, setOrderItems] = useState<OrderItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isGuestOrder, setIsGuestOrder] = useState(false)

  useEffect(() => {
    // Check for guest order access from sessionStorage
    const guestOrderId = sessionStorage.getItem('guest_order_id')
    const guestOrderEmail = sessionStorage.getItem('guest_order_email')

    // If user is not logged in and doesn't have guest order access
    if (!user && !guestOrderId) {
      navigate('/login', { replace: true })
      return
    }

    // If it's a guest and they're trying to access a different order
    if (!user && guestOrderId !== orderId) {
      navigate('/', { replace: true })
      return
    }

    // Mark as guest order if not authenticated
    if (!user && guestOrderId) {
      setIsGuestOrder(true)
    }

    if (orderId) {
      fetchOrderDetails(orderId, guestOrderEmail)
    }
  }, [user, orderId, navigate])

  const fetchOrderDetails = async (id: string, guestEmail?: string | null) => {
    try {
      setLoading(true)
      setError(null)

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY

      // For guests, use anon key; for users, use access token
      const storedSession = user ? localStorage.getItem(`sb-${new URL(supabaseUrl).hostname.split('.')[0]}-auth-token`) : null
      const accessToken = storedSession ? JSON.parse(storedSession).access_token : supabaseKey

      // Build the order fetch URL - for guests, we rely on the RLS policy that allows viewing guest orders
      let orderUrl = `${supabaseUrl}/rest/v1/orders?id=eq.${id}&select=*`

      // For guests, add guest_email filter for extra security
      if (!user && guestEmail) {
        orderUrl += `&guest_email=eq.${encodeURIComponent(guestEmail)}`
      }

      // Fetch order details
      const orderResponse = await fetch(orderUrl, {
        headers: {
          'apikey': supabaseKey,
          'Authorization': `Bearer ${accessToken}`,
        }
      })

      if (!orderResponse.ok) {
        throw new Error('Failed to fetch order details')
      }

      const orderData = await orderResponse.json()
      if (orderData.length === 0) {
        throw new Error('Order not found')
      }

      setOrder(orderData[0])

      // Fetch order items with product names
      const itemsResponse = await fetch(
        `${supabaseUrl}/rest/v1/order_items?order_id=eq.${id}&select=*,product:products(name)`,
        {
          headers: {
            'apikey': supabaseKey,
            'Authorization': `Bearer ${accessToken}`,
          }
        }
      )

      if (!itemsResponse.ok) {
        throw new Error('Failed to fetch order items')
      }

      const itemsData = await itemsResponse.json()
      setOrderItems(itemsData)

    } catch (err) {
      console.error('Error fetching order:', err)
      setError(err instanceof Error ? err.message : 'Failed to load order details')
    } finally {
      setLoading(false)
    }
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen">
        <div className="max-w-3xl mx-auto px-6 py-10">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-16 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-purple-600 mx-auto"></div>
            <p className="mt-4 text-gray-500 text-sm">Loading order details...</p>
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (error || !order) {
    return (
      <div className="min-h-screen">
        <div className="max-w-3xl mx-auto px-6 py-10">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-16 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-8 h-8 text-red-500"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Order Not Found</h3>
            <p className="text-gray-500 text-sm mb-6">{error || 'Unable to find this order.'}</p>
            <Link
              to="/products"
              className="inline-flex items-center px-5 py-2.5 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors"
            >
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-3xl mx-auto px-6 py-10">
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="w-10 h-10 text-green-500"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Order Confirmed!</h1>
          <p className="text-gray-500 text-sm">
            Thank you for your order. Payment processing coming next...
          </p>
        </div>

        {/* Payment Pending Notice */}
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <div className="flex items-start gap-3">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5"
              style={{ width: '24px', height: '24px' }}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
              />
            </svg>
            <div>
              <h3 className="font-semibold text-amber-800">Payment Pending</h3>
              <p className="text-sm text-amber-700 mt-1">
                Your order has been saved. Payment integration is coming soon.
                We'll notify you when it's time to complete your purchase.
              </p>
            </div>
          </div>
        </div>

        {/* Order Details Card */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-6">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h2 className="text-base font-semibold text-gray-900">Order Details</h2>
              <p className="text-xs text-gray-500 mt-0.5">Order #{order.id.slice(0, 8).toUpperCase()}</p>
            </div>
            <span className="px-3 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
              {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
            </span>
          </div>

          {/* Order Items */}
          <div className="divide-y divide-gray-100">
            {orderItems.map((item) => (
              <div key={item.id} className="px-5 py-4 flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-900">{item.product?.name || 'Unknown Product'}</h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Qty: {item.quantity} x ${item.price_at_purchase.toFixed(2)}
                  </p>
                </div>
                <p className="text-sm font-semibold text-gray-900">
                  ${(item.quantity * item.price_at_purchase).toFixed(2)}
                </p>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="px-5 py-4 bg-gray-50 border-t border-gray-100">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Subtotal</span>
                <span className="text-gray-900">${order.shipping_address.subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Shipping</span>
                {order.shipping_address.shipping_cost === 0 ? (
                  <span className="text-green-600 font-medium">FREE</span>
                ) : (
                  <span className="text-gray-900">${order.shipping_address.shipping_cost.toFixed(2)}</span>
                )}
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Tax</span>
                <span className="text-gray-900">${order.shipping_address.tax_amount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-base font-semibold pt-2 border-t border-gray-200">
                <span className="text-gray-900">Total</span>
                <span className="text-gray-900">${order.total_amount.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Shipping Address Card */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-6">
          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-5 h-5 text-gray-500"
                style={{ width: '20px', height: '20px' }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"
                />
              </svg>
              Shipping Address
            </h2>
          </div>
          <div className="p-5">
            <p className="font-medium text-gray-900">{order.shipping_address.full_name}</p>
            <p className="text-sm text-gray-600 mt-1">{order.shipping_address.street_address}</p>
            <p className="text-sm text-gray-600">
              {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.postal_code}
            </p>
            <p className="text-sm text-gray-600">{order.shipping_address.country}</p>
            <p className="text-sm text-gray-500 mt-2">{order.shipping_address.phone}</p>
          </div>
        </div>

        {/* Confirmation Email Notice */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 mb-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-5 h-5 text-purple-600"
                style={{ width: '20px', height: '20px' }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                />
              </svg>
            </div>
            <div>
              <h3 className="font-medium text-gray-900">Confirmation Sent</h3>
              <p className="text-sm text-gray-500 mt-1">
                A confirmation email will be sent to <span className="font-medium text-gray-700">{order.shipping_address.email}</span> once payment is processed.
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Link
            to="/products"
            className="flex-1 py-3 px-6 bg-purple-600 text-white font-semibold text-sm rounded-xl text-center hover:bg-purple-700 transition-colors"
          >
            Continue Shopping
          </Link>
          <Link
            to="/"
            className="flex-1 py-3 px-6 bg-white border border-gray-200 text-gray-700 font-semibold text-sm rounded-xl text-center hover:bg-gray-50 transition-colors"
          >
            Return Home
          </Link>
        </div>

        {/* Create Account CTA for Guest Orders */}
        {isGuestOrder && (
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-6 mt-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-6 h-6 text-purple-600"
                  style={{ width: '24px', height: '24px' }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zM4 19.235v-.11a6.375 6.375 0 0112.75 0v.109A12.318 12.318 0 0110.374 21c-2.331 0-4.512-.645-6.374-1.766z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Create an Account
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Sign up to track this order, view order history, save addresses,
                and enjoy faster checkout on future purchases.
              </p>
              <Link
                to="/signup"
                state={{
                  prefillEmail: order?.guest_email || order?.shipping_address?.email,
                  prefillName: order?.guest_name || order?.shipping_address?.full_name,
                  fromGuestOrder: true,
                  orderId: orderId
                }}
                className="inline-flex items-center px-6 py-3 bg-purple-600 text-white font-semibold text-sm rounded-xl hover:bg-purple-700 transition-colors"
              >
                Create Account with This Order
              </Link>
              <p className="text-xs text-gray-500 mt-3">
                Your order will automatically be linked to your new account.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
